import { ReactElement, useMemo, useState } from 'react';
import type { Data, Layout } from 'plotly.js';
import PlotlyChart from './PlotlyChart';
import { formatNumber } from './breakdownFormat';
import NormalisationSlopePlot from './NormalisationSlopePlot';

interface Props {
  config: any;
  session: any;
  sessionRun?: any;
  selectedTraverse?: number;
  traverseHead: number | null;
  traverseChest: number | null;
  runHead?: number | null;
  runChest?: number | null;
}

interface ScatterPoint {
  head: number;
  chest: number;
  isCurrent: boolean;
}

const HEAD_CHANNEL = 'BodyPosHead_sG3_cm';
const CHEST_CHANNEL = 'BodyPosChest_sG3_cm';

function collectTraversePoints(traverse: any): { heads: number[]; chests: number[] } {
  const heads: number[] = traverse?.[HEAD_CHANNEL] ?? [];
  const chests: number[] = traverse?.[CHEST_CHANNEL] ?? [];
  return { heads, chests };
}

type ViewMode = '3d' | '2d';
type DataLevel = 'traverse' | 'run' | 'config' | 'all';
type NormType = 'standard' | 'flattened';

const SURFACE_HALF_RANGE = 10;  // cm — surface covers ref ± 10 cm in each axis
const SURFACE_STEP = 0.5;

function range(min: number, max: number, step: number): number[] {
  const out: number[] = [];
  for (let v = min; v <= max + 1e-9; v += step) out.push(Number(v.toFixed(3)));
  return out;
}

// Linear-only normalisation. Both axes contribute independently — no parabolic
// term and no cross-term — so the resulting surface is a tilted plane.
function evalNormalisation(
  headCm: number,
  chestCm: number,
  headRef: number,
  chestRef: number,
  headSlope: number,
  chestSlope: number,
): number {
  return (headCm - headRef) * headSlope + (chestCm - chestRef) * chestSlope;
}

export default function NormalisationContourBody({
  config,
  session,
  sessionRun,
  selectedTraverse,
  traverseHead,
  traverseChest,
  runHead = null,
  runChest = null,
}: Props): ReactElement {
  const [viewMode, setViewMode] = useState<ViewMode>('3d');
  const [dataLevel, setDataLevel] = useState<DataLevel>('traverse');
  const [normType, setNormType] = useState<NormType>('standard');

  // Calibration values may be in mm / per-mm (per session_data_normalisation.py:142
  // which explicitly converts to mm for storage) OR may already be in cm / per-cm
  // depending on how the calibration was solved/migrated. The per-traverse arrays
  // we plot are in cm (BodyPosHead_sG3_cm / BodyPosChest_sG3_cm), so we use a
  // value-range heuristic: typical body position sensor readings are 5–50 cm; any
  // ref value above 60 is therefore almost certainly mm and gets divided by 10.
  // The slope conversion is the inverse (× 10 if originally per-mm).
  const headRefRaw: number = config?.head_ref ?? 0;
  const chestRefRaw: number = config?.chest_ref ?? 0;
  const headSlopeRaw: number = config?.head_slope ?? 0;
  const chestSlopeRaw: number = config?.chest_slope ?? 0;
  const headIsMm = Math.abs(headRefRaw) > 60;
  const chestIsMm = Math.abs(chestRefRaw) > 60;
  const headRef: number = headIsMm ? headRefRaw / 10 : headRefRaw;
  const chestRef: number = chestIsMm ? chestRefRaw / 10 : chestRefRaw;
  const headSlope: number = headIsMm ? headSlopeRaw * 10 : headSlopeRaw;
  const chestSlope: number = chestIsMm ? chestSlopeRaw * 10 : chestSlopeRaw;

  // Surface mesh ranges centred on the refs so the plot stays meaningful no
  // matter where the rider's typical body position sits.
  const headMin = headRef - SURFACE_HALF_RANGE;
  const headMax = headRef + SURFACE_HALF_RANGE;
  const chestMin = chestRef - SURFACE_HALF_RANGE;
  const chestMax = chestRef + SURFACE_HALF_RANGE;

  // Compute scatter points based on data level. Each point gets (head, chest) in
  // the same units as the surface axes; we evaluate z on the surface for placement.
  const scatterPoints = useMemo<ScatterPoint[]>(() => {
    const points: ScatterPoint[] = [];
    const sessionRunId = sessionRun?.id ?? sessionRun?._id ?? sessionRun?.run_id;
    const currentConfig = (session?.configs ?? []).find((c: any) =>
      (c?.runs ?? []).some(
        (r: any) => (r?.id ?? r?._id ?? r?.run_id) === sessionRunId,
      ),
    );

    if (dataLevel === 'traverse') {
      const { heads, chests } = collectTraversePoints(sessionRun?.traverse);
      const idx = selectedTraverse ?? 0;
      if (Number.isFinite(heads[idx]) && Number.isFinite(chests[idx])) {
        points.push({ head: heads[idx], chest: chests[idx], isCurrent: true });
      }
    } else if (dataLevel === 'run') {
      const { heads, chests } = collectTraversePoints(sessionRun?.traverse);
      const len = Math.min(heads.length, chests.length);
      for (let i = 0; i < len; i++) {
        if (Number.isFinite(heads[i]) && Number.isFinite(chests[i])) {
          points.push({ head: heads[i], chest: chests[i], isCurrent: i === selectedTraverse });
        }
      }
    } else if (dataLevel === 'config') {
      (currentConfig?.runs ?? []).forEach((r: any) => {
        const isSelectedRun = (r?.id ?? r?._id ?? r?.run_id) === sessionRunId;
        const { heads, chests } = collectTraversePoints(r?.traverse);
        const len = Math.min(heads.length, chests.length);
        for (let i = 0; i < len; i++) {
          if (Number.isFinite(heads[i]) && Number.isFinite(chests[i])) {
            points.push({
              head: heads[i],
              chest: chests[i],
              isCurrent: isSelectedRun && i === selectedTraverse,
            });
          }
        }
      });
    } else {
      (session?.configs ?? []).forEach((c: any) => {
        (c?.runs ?? []).forEach((r: any) => {
          const isSelectedRun = (r?.id ?? r?._id ?? r?.run_id) === sessionRunId;
          const { heads, chests } = collectTraversePoints(r?.traverse);
          const len = Math.min(heads.length, chests.length);
          for (let i = 0; i < len; i++) {
            if (Number.isFinite(heads[i]) && Number.isFinite(chests[i])) {
              points.push({
                head: heads[i],
                chest: chests[i],
                isCurrent: isSelectedRun && i === selectedTraverse,
              });
            }
          }
        });
      });
    }
    return points;
  }, [dataLevel, session, sessionRun, selectedTraverse]);

  const surface = useMemo(() => {
    const heads = range(headMin, headMax, SURFACE_STEP);
    const chests = range(chestMin, chestMax, SURFACE_STEP);
    const z = chests.map((c) =>
      heads.map((h) => evalNormalisation(h, c, headRef, chestRef, headSlope, chestSlope)),
    );
    let zMin = Infinity;
    let zMax = -Infinity;
    z.forEach((row) => row.forEach((v) => {
      if (Number.isFinite(v)) {
        if (v < zMin) zMin = v;
        if (v > zMax) zMax = v;
      }
    }));
    if (!Number.isFinite(zMin)) zMin = 0;
    if (!Number.isFinite(zMax)) zMax = 0;
    return { x: heads, y: chests, z, zMin, zMax };
  }, [headMin, headMax, chestMin, chestMax, headRef, chestRef, headSlope, chestSlope]);

  const traverseZ =
    traverseHead != null && traverseChest != null
      ? evalNormalisation(traverseHead, traverseChest, headRef, chestRef, headSlope, chestSlope)
      : null;
  const runZ =
    runHead != null && runChest != null
      ? evalNormalisation(runHead, runChest, headRef, chestRef, headSlope, chestSlope)
      : null;

  const labelZOffset = Math.max(Math.abs(surface.zMax - surface.zMin) * 0.18, 0.02);

  const headStep = (headMax - headMin) / 4;
  const chestStep = (chestMax - chestMin) / 4;

  const data = useMemo<Data[]>(() => {
    const traces: Data[] = [
      {
        type: 'surface',
        x: surface.x,
        y: surface.y,
        z: surface.z,
        colorscale: 'Viridis',
        opacity: 0.85,
        showscale: true,
        contours: {
          x: { show: true, color: '#374151', width: 1, start: headMin, end: headMax, size: headStep },
          y: { show: true, color: '#374151', width: 1, start: chestMin, end: chestMax, size: chestStep },
        },
        hoverinfo: 'skip',
        name: 'Normalisation',
      } as Data,
    ];

    // Reference marker + label (label raised in Z, includes values)
    traces.push({
      type: 'scatter3d',
      mode: 'markers',
      x: [headRef],
      y: [chestRef],
      z: [0],
      marker: { size: 6, color: '#000000', symbol: 'diamond' },
      hoverinfo: 'skip',
      showlegend: false,
    });
    traces.push({
      type: 'scatter3d',
      mode: 'text',
      x: [headRef],
      y: [chestRef],
      z: [labelZOffset],
      text: [`Reference: ${formatNumber(headRef, 1)} cm head, ${formatNumber(chestRef, 1)} cm chest`],
      textfont: { color: '#000000', size: 12 },
      textposition: 'middle right',
      hoverinfo: 'skip',
      showlegend: false,
    });

    // Other-traverse markers (gray dots, lifted above surface) for run/config/all data levels
    const others = scatterPoints.filter((p) => !p.isCurrent);
    if (others.length > 0) {
      traces.push({
        type: 'scatter3d',
        mode: 'markers',
        x: others.map((p) => p.head),
        y: others.map((p) => p.chest),
        z: others.map(
          (p) => evalNormalisation(p.head, p.chest, headRef, chestRef, headSlope, chestSlope) + labelZOffset * 0.3,
        ),
        marker: { size: 4, color: '#9ca3af', opacity: 0.7 },
        hoverinfo: 'skip',
        showlegend: false,
      });
    }

    if (traverseZ != null && traverseHead != null && traverseChest != null) {
      // Traverse marker + label (label raised in Z, includes values)
      traces.push({
        type: 'scatter3d',
        mode: 'markers',
        x: [traverseHead],
        y: [traverseChest],
        z: [traverseZ],
        marker: { size: 8, color: '#ef4444' },
        hoverinfo: 'skip',
        showlegend: false,
      });
      traces.push({
        type: 'scatter3d',
        mode: 'text',
        x: [traverseHead],
        y: [traverseChest],
        z: [traverseZ + labelZOffset],
        text: [`Traverse: ${formatNumber(traverseHead, 2)} cm head, ${formatNumber(traverseChest, 2)} cm chest`],
        textfont: { color: '#b91c1c', size: 12 },
        textposition: 'middle right',
        hoverinfo: 'skip',
        showlegend: false,
      });

      // Walk a step-path *in space* with the horizontal legs always at the *upper* z, so the
      // path stays above the slope regardless of the sign of the normalisation. When positive,
      // legs sit at z=traverseZ and the vertical drops to the reference (z=0). When negative,
      // legs sit at z=0 and the vertical drops down to the traverse (at z=traverseZ).
      if (traverseZ !== 0) {
        const lineColor = '#1e3a8a';
        const positive = traverseZ > 0;
        const upperZ = positive ? traverseZ : 0;

        const startXY: [number, number] = positive
          ? [traverseHead, traverseChest]
          : [headRef, chestRef];
        const cornerXY: [number, number] = positive
          ? [traverseHead, chestRef]
          : [traverseHead, chestRef];
        const endXY: [number, number] = positive
          ? [headRef, chestRef]
          : [traverseHead, traverseChest];
        const lowerXYZ: [number, number, number] = positive
          ? [headRef, chestRef, 0]
          : [traverseHead, traverseChest, traverseZ];

        // Leg 1: first horizontal leg at z=upperZ (parallel to one axis).
        traces.push({
          type: 'scatter3d',
          mode: 'lines',
          x: [startXY[0], cornerXY[0]],
          y: [startXY[1], cornerXY[1]],
          z: [upperZ, upperZ],
          line: { color: lineColor, width: 4 },
          hoverinfo: 'skip',
          showlegend: false,
        });

        // Leg 2: second horizontal leg at z=upperZ (parallel to the other axis).
        traces.push({
          type: 'scatter3d',
          mode: 'lines',
          x: [cornerXY[0], endXY[0]],
          y: [cornerXY[1], endXY[1]],
          z: [upperZ, upperZ],
          line: { color: lineColor, width: 4 },
          hoverinfo: 'skip',
          showlegend: false,
        });

        // Leg 3: vertical difference — drop (or rise) from the theoretical point at upperZ
        // to the lower point on the surface. Length equals |traverseZ|.
        traces.push({
          type: 'scatter3d',
          mode: 'lines',
          x: [endXY[0], lowerXYZ[0]],
          y: [endXY[1], lowerXYZ[1]],
          z: [upperZ, lowerXYZ[2]],
          line: { color: lineColor, width: 4, dash: 'dot' },
          hoverinfo: 'skip',
          showlegend: false,
        });

        // Diamond markers at the two in-space corners (where the legs join).
        traces.push({
          type: 'scatter3d',
          mode: 'markers',
          x: [cornerXY[0], endXY[0]],
          y: [cornerXY[1], endXY[1]],
          z: [upperZ, upperZ],
          marker: { size: 5, color: '#000000', symbol: 'diamond' },
          hoverinfo: 'skip',
          showlegend: false,
        });
      }
    }

    // Run-level marker (orange) — used by the Crr view to show the run-aggregate
    // body position alongside the per-traverse and reference markers.
    if (runZ != null && runHead != null && runChest != null) {
      traces.push({
        type: 'scatter3d',
        mode: 'markers',
        x: [runHead],
        y: [runChest],
        z: [runZ],
        marker: { size: 8, color: '#f59e0b', symbol: 'square' },
        hoverinfo: 'skip',
        showlegend: false,
      });
      traces.push({
        type: 'scatter3d',
        mode: 'text',
        x: [runHead],
        y: [runChest],
        z: [runZ + labelZOffset],
        text: [`Run: ${formatNumber(runHead, 2)} cm head, ${formatNumber(runChest, 2)} cm chest`],
        textfont: { color: '#b45309', size: 12 },
        textposition: 'middle right',
        hoverinfo: 'skip',
        showlegend: false,
      });
    }
    return traces;
  }, [
    surface,
    headRef,
    chestRef,
    headSlope,
    chestSlope,
    traverseHead,
    traverseChest,
    traverseZ,
    runHead,
    runChest,
    runZ,
    labelZOffset,
    headMin,
    headMax,
    chestMin,
    chestMax,
    headStep,
    chestStep,
    scatterPoints,
  ]);

  const layout = useMemo<Partial<Layout>>(() => ({
    scene: {
      xaxis: { title: { text: 'Head Position (cm)' }, nticks: 5, showspikes: false },
      yaxis: { title: { text: 'Chest Position (cm)' }, nticks: 5, showspikes: false },
      zaxis: { title: { text: 'Normalisation amount' }, nticks: 5, showspikes: false },
    },
    margin: { l: 0, r: 0, b: 0, t: 10 },
    showlegend: false,
    hovermode: false,
    autosize: true,
  }), []);

  const slopesAvailable = headSlope !== 0 || chestSlope !== 0;

  const plotData = session?.plot_data;
  const configs = session?.configs;

  return (
    <div className="cr-rounded-lg cr-bg-white cr-p-6 cr-shadow-md cr-mb-6">
      <div className="cr-flex cr-items-center cr-justify-between cr-mb-3 cr-flex-wrap cr-gap-3">
        <h3 className="font-montserrat cr-text-lg cr-font-semibold cr-text-blue-900">
          Body Position Normalisation Contour
        </h3>
        <div className="cr-flex cr-items-center cr-gap-4 cr-text-sm">
          <label className="cr-flex cr-items-center cr-gap-1.5 cr-cursor-pointer">
            <input
              type="radio"
              name="body-normalisation-view-mode"
              value="3d"
              checked={viewMode === '3d'}
              onChange={() => setViewMode('3d')}
              className="cr-w-4 cr-h-4"
            />
            <span className="cr-font-medium">3-D Contour</span>
          </label>
          <label className="cr-flex cr-items-center cr-gap-1.5 cr-cursor-pointer">
            <input
              type="radio"
              name="body-normalisation-view-mode"
              value="2d"
              checked={viewMode === '2d'}
              onChange={() => setViewMode('2d')}
              className="cr-w-4 cr-h-4"
            />
            <span className="cr-font-medium">2-D Slopes</span>
          </label>
        </div>
      </div>
      <p className="cr-text-sm cr-text-gray-500 cr-mb-4">
        How raw CdA gets adjusted at any (head pos, chest pos). The black diamond is the reference
        body position (where no normalisation is needed); the red marker is where this traverse
        actually was. Both axes are linear so the surface is a tilted plane with no curvature.
      </p>
      {!slopesAvailable && (
        <div className="cr-text-amber-700 cr-bg-amber-50 cr-rounded cr-p-3 cr-mb-4 cr-text-sm">
          This config has no head or chest slope set, so the surface is flat and no
          normalisation is applied.
        </div>
      )}
      <div className="cr-flex cr-items-center cr-gap-4 cr-flex-wrap cr-mb-3 cr-text-sm cr-bg-gray-50 cr-rounded cr-px-3 cr-py-2">
        <span className="cr-font-medium cr-text-gray-700">Show:</span>
        {(['traverse', 'run', 'config', 'all'] as DataLevel[]).map((lvl) => (
          <label key={lvl} className="cr-flex cr-items-center cr-gap-1.5 cr-cursor-pointer">
            <input
              type="radio"
              name="body-normalisation-data-level"
              value={lvl}
              checked={dataLevel === lvl}
              onChange={() => setDataLevel(lvl)}
              className="cr-w-4 cr-h-4"
            />
            <span>{lvl === 'all' ? 'all data' : lvl}</span>
          </label>
        ))}
        {dataLevel === 'all' && (
          <div className="cr-flex cr-items-center cr-gap-3 cr-pl-4 cr-border-l cr-border-gray-300">
            <span className="cr-text-gray-500 cr-italic">normalisation:</span>
            {(['standard', 'flattened'] as NormType[]).map((nt) => (
              <label key={nt} className="cr-flex cr-items-center cr-gap-1.5 cr-cursor-pointer">
                <input
                  type="radio"
                  name="body-normalisation-norm-type"
                  value={nt}
                  checked={normType === nt}
                  onChange={() => setNormType(nt)}
                  className="cr-w-4 cr-h-4"
                />
                <span>{nt}</span>
              </label>
            ))}
          </div>
        )}
      </div>
      {viewMode === '3d' ? (
        <PlotlyChart data={data} layout={layout} height={520} />
      ) : (
        <Slopes2DBody
          plotData={plotData}
          configs={configs}
          session={session}
          sessionRun={sessionRun}
          selectedTraverse={selectedTraverse ?? 0}
          normType={dataLevel === 'all' ? normType : 'standard'}
          dataLevel={dataLevel}
          headRef={headRef}
          chestRef={chestRef}
        />
      )}
      <ParameterEquationsBody
        traverseHead={traverseHead}
        traverseChest={traverseChest}
        headRef={headRef}
        chestRef={chestRef}
        headSlope={headSlope}
        chestSlope={chestSlope}
      />
    </div>
  );
}

// Helper: average of finite numbers in an array, or 0 if no finite values.
function meanFinite(arr: number[] | undefined): number {
  if (!arr || arr.length === 0) return 0;
  let sum = 0;
  let n = 0;
  for (const v of arr) {
    if (Number.isFinite(v)) {
      sum += v;
      n++;
    }
  }
  return n > 0 ? sum / n : 0;
}

// Per-plot Y-channel used to compute the flattened-mode config offset (matches
// the channel each plot uses on its Y axis — see NormalisationSlopePlot.tsx).
const FLATTEN_Y_CHANNEL: Record<'head_plot' | 'chest_plot', string> = {
  head_plot: 'CdAXWnNC_xG3',
  chest_plot: 'CdAXWnNH_xG3',
};

function Slopes2DBody({
  plotData,
  configs,
  session,
  sessionRun,
  selectedTraverse,
  normType,
  dataLevel,
  headRef,
  chestRef,
}: {
  plotData: any;
  configs: any[] | undefined;
  session: any;
  sessionRun: any;
  selectedTraverse: number;
  normType: NormType;
  dataLevel: DataLevel;
  headRef: number;
  chestRef: number;
}): ReactElement {
  // Resolve which config index in session.configs contains the selected run.
  const configIdx = useMemo(() => {
    const sessionRunId = sessionRun?.id ?? sessionRun?._id ?? sessionRun?.run_id;
    if (sessionRunId == null) return -1;
    const list = session?.configs ?? [];
    return list.findIndex((c: any) =>
      (c?.runs ?? []).some((r: any) => (r?.id ?? r?._id ?? r?.run_id) === sessionRunId),
    );
  }, [session, sessionRun]);

  // Flattened-mode offset for the selected run's config, per plot type. Same
  // construction as NormalisationContour's Slopes2D — mean(config[i].channel)
  // − mean(configA.channel). Config A always has offset 0.
  const flattenedOffsets = useMemo(() => {
    const list: any[] = session?.configs ?? [];
    const computeFor = (plotType: 'head_plot' | 'chest_plot') => {
      const channel = FLATTEN_Y_CHANNEL[plotType];
      const configMean = (cfg: any): number => {
        const all: number[] = [];
        (cfg?.runs ?? []).forEach((r: any) => {
          const arr: number[] = r?.traverse?.[channel] ?? [];
          arr.forEach((v) => {
            if (Number.isFinite(v)) all.push(v);
          });
        });
        return meanFinite(all);
      };
      const meanA = list[0] ? configMean(list[0]) : 0;
      return list.map((cfg: any) => configMean(cfg) - meanA);
    };
    return {
      head_plot: computeFor('head_plot'),
      chest_plot: computeFor('chest_plot'),
    };
  }, [session]);

  if (!plotData || !configs) {
    return (
      <div className="cr-p-6 cr-text-gray-500 cr-italic">
        2-D slope plots are not available for this session.
      </div>
    );
  }
  const headOffset = flattenedOffsets.head_plot[configIdx] ?? 0;
  const chestOffset = flattenedOffsets.chest_plot[configIdx] ?? 0;
  return (
    <div className="cr-grid cr-grid-cols-1 lg:cr-grid-cols-2 cr-gap-4">
      <NormalisationSlopePlot
        plotType="head_plot"
        dataLevel={dataLevel}
        session={session}
        plotData={plotData?.head_plot?.[normType] ?? plotData?.head_plot?.standard ?? null}
        configIdx={configIdx}
        currentRun={sessionRun}
        selectedTraverse={selectedTraverse}
        configs={configs}
        xRef={headRef}
        normType={normType}
        flattenedOffset={headOffset}
      />
      <NormalisationSlopePlot
        plotType="chest_plot"
        dataLevel={dataLevel}
        session={session}
        plotData={plotData?.chest_plot?.[normType] ?? plotData?.chest_plot?.standard ?? null}
        configIdx={configIdx}
        currentRun={sessionRun}
        selectedTraverse={selectedTraverse}
        configs={configs}
        xRef={chestRef}
        normType={normType}
        flattenedOffset={chestOffset}
      />
    </div>
  );
}

interface ParamEqProps {
  traverseHead: number | null;
  traverseChest: number | null;
  headRef: number;
  chestRef: number;
  headSlope: number;
  chestSlope: number;
}

function ParameterEquationsBody({
  traverseHead,
  traverseChest,
  headRef,
  chestRef,
  headSlope,
  chestSlope,
}: ParamEqProps): ReactElement {
  const headDiff =
    traverseHead != null ? (traverseHead - headRef) * headSlope : null;
  const chestDiff =
    traverseChest != null ? (traverseChest - chestRef) * chestSlope : null;
  const total =
    headDiff != null && chestDiff != null ? headDiff + chestDiff : null;

  return (
    <div className="cr-mt-5 cr-pt-4 cr-border-t cr-border-gray-200 cr-text-sm">
      <div className="cr-grid cr-grid-cols-1 md:cr-grid-cols-2 cr-gap-6 cr-mb-4">
        <EquationRow
          label="Head difference"
          equation="(head_pos − head_ref) × head_slope"
          substitution={
            traverseHead != null
              ? `(${formatNumber(traverseHead, 2)} − ${formatNumber(headRef, 1)}) × ${formatNumber(headSlope, 6)}`
              : null
          }
          result={headDiff}
        />
        <EquationRow
          label="Chest difference"
          equation="(chest_pos − chest_ref) × chest_slope"
          substitution={
            traverseChest != null
              ? `(${formatNumber(traverseChest, 2)} − ${formatNumber(chestRef, 1)}) × ${formatNumber(chestSlope, 6)}`
              : null
          }
          result={chestDiff}
        />
      </div>
      <div className="cr-pt-3 cr-border-t cr-border-gray-200 cr-flex cr-flex-wrap cr-items-baseline cr-gap-x-2">
        <span className="cr-font-medium cr-text-blue-900">Total normalisation</span>
        <span className="cr-font-mono cr-text-gray-700">= head_diff + chest_diff</span>
        {headDiff != null && chestDiff != null && total != null && (
          <span className="cr-font-mono cr-text-gray-700">
            = {formatNumber(headDiff, 6)} + {formatNumber(chestDiff, 6)}
            <span className="cr-text-blue-900 cr-font-semibold"> = {formatNumber(total, 6)}</span>
          </span>
        )}
      </div>
    </div>
  );
}

function EquationRow({
  label,
  equation,
  substitution,
  result,
}: {
  label: string;
  equation: string;
  substitution: string | null;
  result: number | null;
}): ReactElement {
  return (
    <div>
      <div className="cr-font-medium cr-text-gray-800">{label}</div>
      <div className="cr-font-mono cr-text-gray-700 cr-mt-1">{equation}</div>
      {substitution && (
        <div className="cr-font-mono cr-text-gray-600 cr-mt-1">= {substitution}</div>
      )}
      {result != null && (
        <div className="cr-font-mono cr-mt-1 cr-text-gray-900 cr-font-medium">
          = {formatNumber(result, 6)}
        </div>
      )}
    </div>
  );
}
